import argumentSelectors from '@/redux/selectors/argumentSelectors';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import getLastArrayStepArgumentIndex from '@/lib/arguments/getLastArrayStepArgumentIndex';
import { randomId } from '@shared/utils/randomId.js';
import getSelectedArgDetails from '@/lib/arguments/getSelectedArgDetails';
import getStepArrayOptions from '@/lib/arguments/getStepArrayOptions';
import argumentDataSources from '@/schema/argumentDataSources';
import useStepsList from '@/screens/workflows/components/workflowInputVariablePane/useStepsList';
import stepActions from '@/screens/workflows/redux/actions/stepActions';
import appActions from '@/screens/apps/redux/actions/appActions';
import confirmDialog from '@/lib/confirmDialog';
import IconV2 from '@/components/v2/IconV2';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import { AddIcon, DeleteIcon } from '@/lib/constants/icons';
import ArgumentRawObjectButton from './ArgumentRawObjectButton';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';

const ArgumentInputArray = ({ disabled, definition, argument, dataSource, dataSourcePath, dataSavePath, index }) => {
  const id = useRef(randomId());
  const values = useSelector(argumentSelectors[dataSource].activeArguments());
  const stepDataSources = [argumentDataSources.workflowStep, argumentDataSources.workflowVariables, `${argumentDataSources.workflowStep}-customArguments`];
  const steps = stepDataSources.includes(dataSource) ? useStepsList({ search: false }) : [];
  const actions = stepDataSources.includes(dataSource) ? stepActions : appActions;
  const isInWorkflow = stepDataSources.includes(dataSource);

  const isRow = argument?.row === true;
  const isArrayMap = argument?.arrayMap;
  const selectedArgDetails = isArrayMap ? getSelectedArgDetails(argument?.key, values, steps) : null;
  const stepArrayOptions = getStepArrayOptions(steps);
  const hasChildren = argument?.children?.length > 0;
  const parsedArgumentKey = argument?.key?.replace(/\[\d+\]/g, '[]');
  const hasChildrenByDefinition = definition?.arguments?.some((n) => n.key !== parsedArgumentKey && n?.key?.startsWith(parsedArgumentKey));

  const handleAddArrayRow = (event) => {
    event.stopPropagation();
    const lastIndex = getLastArrayStepArgumentIndex(argument, values);
    const baseKey = `${argument.key.replace(/\[\](?=[^\\[\]]*$)/, '')}[${lastIndex + 1}]`;
    actions.set(dataSavePath ? `${dataSavePath}.arguments.["${baseKey}"]` : `${dataSourcePath}.["${baseKey}"]`, { type: 'array', id: randomId() });
  };

  const handleDeleteArrayRow = (event) => {
    event.stopPropagation();
    const regexString = /\[(\d+)\](?=[^\\[\]]*$)/;
    const removedIndex = argument.key.match(regexString)?.[1];
    const modifiedKey = argument.key.replace(regexString, '');
    const newArgs = { ...(values || {}) };
    for (const key in newArgs) {
      if (key.startsWith(`${argument?.key}`) || key === '' || String(key).includes('[undefined]')) {
        delete newArgs[key];
      }

      const currentIndex = key.match(regexString)?.[1];
      if (key.startsWith(`${modifiedKey}`) && currentIndex > removedIndex) {
        delete newArgs[key];
        const newKey = key.replace(regexString, `[${currentIndex - 1}]`);
        newArgs[newKey] = values[key];
      }
    }

    actions.set(dataSavePath ? `${dataSavePath}.arguments` : dataSourcePath, { ...newArgs });
  };

  const handleDeleteArrayMap = async (event) => {
    event.stopPropagation();
    const confirm = await confirmDialog({
      title: 'Are you sure you want to remove this mapper?',
      message: 'This cannot be undone.'
    });
    if (confirm === true) {
      const newArgs = { ...(values || {}) };

      for (const key in newArgs) {
        if (key.startsWith(`${argument.key}`) || key === '' || String(key).includes('[undefined]')) {
          delete newArgs[key];
        }
      }

      actions.set(dataSavePath ? `${dataSavePath}.arguments` : dataSourcePath, { ...newArgs });
    }
  };

  const handleSelectChange = ({ value }) => {
    actions.set(dataSavePath ? `${dataSavePath}.arguments.["${argument.key}"]` : `${dataSourcePath}.["${argument.key}"]`, value);
  };

  if (disabled) {
    return null;
  }

  return (
    <div className={`row-item-options ${selectedArgDetails ? 'is-mapping' : ''}`}>
      {(isArrayMap || (!hasChildren && !isRow && hasChildrenByDefinition)) && isInWorkflow && (
        <div className={`map-array-button map-button-${id.current}`} title={selectedArgDetails ? `This argument is mapping Step ${selectedArgDetails?.number}: "${selectedArgDetails?.label}"` : 'Map this argument to a value from another step'}>
          <IconV2 icon={selectedArgDetails?.icon || utilityAppIcons.map} className={selectedArgDetails ? '' : 'inactive'} />
          {selectedArgDetails?.number && <div className="map-array-button-step-number">{selectedArgDetails.number}</div>}
          {selectedArgDetails?.label && <span className="map-array-button-label">{selectedArgDetails.label}</span>}
          {selectedArgDetails?.map && <div className="map-array-button-badge">MAP</div>}
          <FloatingSelectV2 triggers={[`.map-button-${id.current}`]} options={stepArrayOptions} useSearch={false} onChange={handleSelectChange} width={300} />
        </div>
      )}
      {!isArrayMap && !isRow && (
        <>
          <ArgumentRawObjectButton actions={actions} argument={argument} dataSource={dataSource} dataSourcePath={dataSourcePath} dataSavePath={dataSavePath} />
          {hasChildrenByDefinition && <IconV2 icon={AddIcon} className={'add'} variant={'inactive'} onClick={handleAddArrayRow} title="Add a row" />}
        </>
      )}
      {isArrayMap && <IconV2 icon={DeleteIcon} className={'delete'} variant={'inactive'} onClick={handleDeleteArrayMap} />}
      {isRow && <IconV2 icon={DeleteIcon} className={'delete'} variant={'inactive'} onClick={handleDeleteArrayRow} />}
    </div>
  );
};

export default ArgumentInputArray;
